/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import { Disclosure } from '@headlessui/react'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { ChangeEvent, ReactElement, useState } from 'react'
import { ChevronToggle } from '../ChevronToggle'
import { PipelineIcon } from '../Icon/PipelineIcon'
import { Button } from 'app/components/ui/button'
import DraggableItem from '../sortable-dnd/DraggableItem'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import type { StageType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'

// A `system` stage is built in: always shown, so it has no hide, only a place in the order.
type EditableStage = StageType & { visible: boolean; system?: boolean | null }

export const EditModal = ({ stages, handleStageChange }) => {
  const [searchValue, setSearchValue] = useState<string>(``)
  const [visibleStages, setVisibleStages] = useState<EditableStage[]>(stages)
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    let searchValue = event.target.value
    setSearchValue(searchValue)
  }
  const reorderStage = (dragIndex: number, targetInex: number) => {
    setVisibleStages((prev) => {
      const stages: EditableStage[] = [...(prev ?? [])]
      let newOrder = [...stages]
      const from = stages[dragIndex]
      const to = stages[targetInex]
      newOrder[dragIndex] = to
      newOrder[targetInex] = from
      newOrder = [...newOrder].map((stage, i) => ({ ...stage, order: i + 1 }))
      return newOrder
    })
  }
  const changeVisibility = (stageName, checked) => {
    let stages = visibleStages.filter((s) => s.visible)
    if (stages.length === 1 && checked === false) {
      notifyError(`At least one stage should be visible`)
      return
    }
    setVisibleStages((prev) =>
      prev.map((s) => {
        if (s.name === stageName) {
          return { ...s, visible: checked }
        }
        return s
      }),
    )
  }

  return (
    <>
      <div className="flex max-h-[80vh] w-[720px] pb-4">
        <div className="w-1/2 border-r border-default">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-6 w-6 text-default" aria-hidden="true" />
            </div>
            <input
              className="h-11 w-full border-b border-default bg-surface py-3 pl-11 text-body-sm text-strong outline-none"
              placeholder="Search..."
              value={searchValue}
              onChange={handleSearch}
            />
          </div>
          <div className="h-[90%] overflow-y-auto">
            <Disclosure>
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex w-full justify-between px-4 py-3 text-strong">
                    <div className="text-ui-sm">Stages</div>
                    <ChevronToggle open={open} />
                  </Disclosure.Button>
                  <Disclosure.Panel className="w-full" static>
                    {visibleStages
                      ?.filter(({ name }) => {
                        if (searchValue === ``) return true
                        return name.toLowerCase().includes(searchValue.toLowerCase())
                      })
                      ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      ?.map(({ name, visible, system }) => {
                        return (
                          <div key={name} className="py-2 pl-8">
                            <label className="mb-0 flex cursor-pointer items-center">
                              <input
                                {...{
                                  type: `checkbox`,
                                  checked: visible,
                                  disabled: !!system,
                                  onChange: (evt) => changeVisibility(name, evt.target.checked),
                                  className: `mr-2 cursor-pointer fill-current text-default rounded-sm h-4 w-4 disabled:cursor-not-allowed disabled:bg-surface-subtle disabled:text-subtle disabled:bg-current`,
                                }}
                              />
                              <div aria-label={name} className="text-body-sm text-default">
                                {name}
                              </div>
                            </label>
                          </div>
                        )
                      })}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
        </div>
        <div className="w-1/2 overflow-y-auto bg-surface-subtle py-3 pl-3.5 pr-4">
          <div className="align-middle text-ui-sm text-strong">
            Selected stages ({visibleStages.filter(({ visible }) => visible)?.length || 0})
          </div>
          <div className="mt-3 flex flex-col space-y-2">
            {visibleStages
              ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              ?.map(({ name, visible, system }, i) => (
                <div key={name} className={`${visible ? `block` : `hidden`}`}>
                  <DraggableItem
                    id={name}
                    index={i}
                    moveCard={reorderStage}
                    onRemove={
                      system
                        ? undefined
                        : (index) =>
                            setVisibleStages((prev) => {
                              let visibility = [...prev]
                              visibility[index].visible = false
                              return visibility
                            })
                    }
                  >
                    <div className="flex items-center">{name}</div>
                  </DraggableItem>
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className="flex justify-end border-t border-default p-6">
        <button aria-label="Cancel" onClick={closeModal} className="btn btn-secondary btn-md">
          Cancel
        </button>
        <button
          onClick={() => {
            handleStageChange(visibleStages)
            closeModal()
          }}
          className="btn btn-primary btn-md ml-4"
        >
          Save
        </button>
      </div>
    </>
  )
}

export function EditStages({
  stages,
  onActionTriggered,
  plain = false,
  title = `Edit stages`,
}: {
  stages: (StageType & { visible: boolean })[]
  onActionTriggered: (stages: (StageType & { visible: boolean })[]) => void
  /** Text only at every width, for a column of text buttons such as a list's Settings. */
  plain?: boolean
  title?: string | ReactElement
}): ReactElement {
  const isMobile = useIsMobile()
  return (
    <Button
      variant="tertiary"
      size="sm"
      aria-label="Edit stages"
      onClick={() =>
        showModal(<EditModal stages={stages} handleStageChange={onActionTriggered} />, {
          title,
          isFullScreen: false,
          actionLabel: `Save`,
          useRadix: true,
        })
      }
    >
      {!plain && <PipelineIcon className="h-4 w-4" />}
      {(plain || !isMobile) && `Edit stages`}
    </Button>
  )
}
